import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usersAPI, authAPI, notificationsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ThemeSelector from '../components/ThemeSelector';
import {
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  CheckIcon,
  KeyIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  SwatchIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const ADMIN_ROLES = ['org_admin', 'super_admin'];

const tabs = [
  { id: 'profile', name: 'Profile', icon: UserIcon },
  { id: 'appearance', name: 'Appearance', icon: SwatchIcon },
  { id: 'notifications', name: 'Notifications', icon: BellIcon },
  { id: 'security', name: 'Security', icon: ShieldCheckIcon },
  { id: 'organization', name: 'Organization', icon: BuildingOfficeIcon },
  { id: 'mail-alerts', name: 'Mail Alerts', icon: EnvelopeIcon, adminOnly: true },
];

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuthStore();
  const visibleTabs = tabs.filter(tab => !tab.adminOnly || ADMIN_ROLES.includes(user?.role));

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-description">Manage your account and preferences</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="glass-card p-2">
            <nav className="space-y-1">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200',
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-[var(--theme-100)] to-[var(--theme-50)] text-[var(--theme-700)] border border-[var(--theme-200)]'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  )}
                >
                  <tab.icon className={clsx(
                    'w-5 h-5',
                    activeTab === tab.id && 'text-[var(--theme-600)]'
                  )} />
                  <span className="font-medium">{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="glass-card p-6 animate-scale-in" style={{ animationDelay: '0.2s' }}>
            {activeTab === 'profile' && <ProfileSettings user={user} />}
            {activeTab === 'appearance' && <AppearanceSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'security' && <SecuritySettings />}
            {activeTab === 'organization' && <OrganizationSettings user={user} />}
            {activeTab === 'mail-alerts' && ADMIN_ROLES.includes(user?.role) && <MailAlertsSettings />}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileSettings({ user }) {
  const { refreshUser } = useAuthStore();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await usersAPI.updateProfile(user.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
      });
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-800">Profile Information</h2>
      </div>

      {/* Avatar Section */}
      <div className="flex items-center gap-6 p-4 glass rounded-xl">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
          <span className="text-2xl font-bold text-white">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </span>
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-slate-800">{user?.firstName} {user?.lastName}</h3>
          <p className="text-sm text-slate-500">{user?.email}</p>
        </div>
        <button type="button" className="btn btn-secondary">
          Change Photo
        </button>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">First Name</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">Last Name</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            className="input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">Email</label>
          <input
            type="email"
            value={formData.email}
            disabled
            className="input opacity-50 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-2">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="input"
            placeholder="+1 (555) 000-0000"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

function NotificationSettings() {
  const { user, refreshUser } = useAuthStore();
  const [settings, setSettings] = useState(
    user?.notificationPrefs || {
      email: { critical: true, high: true, medium: false, low: false },
      push: { critical: true, high: true, medium: true, low: false },
      sms: { critical: true, high: false, medium: false, low: false },
    }
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = (channel, severity) => {
    setSettings(prev => ({
      ...prev,
      [channel]: { ...prev[channel], [severity]: !prev[channel][severity] }
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await usersAPI.updateNotificationPrefs(user.id, settings);
      await refreshUser();
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const channelIcons = {
    email: EnvelopeIcon,
    push: DevicePhoneMobileIcon,
    sms: ComputerDesktopIcon,
  };

  const severityConfig = {
    critical: { color: 'text-red-400', bg: 'bg-red-500/20' },
    high: { color: 'text-orange-400', bg: 'bg-orange-500/20' },
    medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    low: { color: 'text-blue-400', bg: 'bg-blue-500/20' },
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-800">Notification Preferences</h2>
      <p className="text-slate-500">Choose how you want to receive alerts based on severity</p>

      <div className="space-y-4">
        {Object.entries(settings).map(([channel, severities], channelIndex) => {
          const Icon = channelIcons[channel];
          return (
            <div
              key={channel}
              className="glass rounded-xl p-4 animate-fade-in"
              style={{ animationDelay: `${channelIndex * 0.1}s` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <span className="font-medium text-slate-800 capitalize">{channel}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {['critical', 'high', 'medium', 'low'].map(severity => {
                  const config = severityConfig[severity];
                  const isEnabled = severities[severity];
                  return (
                    <button
                      key={severity}
                      onClick={() => handleToggle(channel, severity)}
                      className={clsx(
                        'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300',
                        isEnabled
                          ? `${config.bg} ${config.color} border border-current`
                          : 'bg-slate-50 text-slate-400 border border-slate-200 hover:border-slate-300'
                      )}
                    >
                      {isEnabled && <CheckIcon className="w-4 h-4" />}
                      <span className="capitalize">{severity}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} className="btn btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}

function SecuritySettings() {
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setIsChangingPassword(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwords.current,
        newPassword: passwords.new,
      });
      toast.success('Password changed successfully');
      setPasswords({ current: '', new: '', confirm: '' });
      setShowPasswords({ current: false, new: false, confirm: false });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <div className="space-y-8">
      {/* Change Password Section */}
      <div>
        <h2 className="text-xl font-semibold text-slate-800 mb-6">Change Password</h2>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Current Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <KeyIcon className="w-5 h-5 text-slate-500" />
              </div>
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="input pl-12 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-slate-700 transition-colors"
                tabIndex="-1"
              >
                {showPasswords.current ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">New Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <KeyIcon className="w-5 h-5 text-slate-500" />
              </div>
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="input pl-12 pr-12"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-slate-700 transition-colors"
                tabIndex="-1"
              >
                {showPasswords.new ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Confirm New Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <KeyIcon className="w-5 h-5 text-slate-500" />
              </div>
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="input pl-12 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-500 hover:text-slate-700 transition-colors"
                tabIndex="-1"
              >
                {showPasswords.confirm ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={isChangingPassword}>
            {isChangingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>

      {/* Divider */}
      <div className="border-t border-slate-200" />

      {/* Active Sessions */}
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Active Sessions</h3>
        <div className="space-y-3">
          <div className="glass rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <ComputerDesktopIcon className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium text-slate-800">Current Session</p>
                <p className="text-sm text-slate-500">Chrome on macOS - Active now</p>
              </div>
            </div>
            <span className="badge badge-success">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrganizationSettings({ user }) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-800">Organization</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <label className="block text-sm font-medium text-slate-500 mb-2">Organization Name</label>
          <p className="text-slate-800 font-medium">{user?.organization?.name || 'KenSafety Inc.'}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <label className="block text-sm font-medium text-slate-500 mb-2">Your Role</label>
          <p className="text-slate-800 font-medium capitalize">{user?.role || 'Admin'}</p>
        </div>
      </div>

      <div className="glass rounded-xl p-4 border border-blue-500/30">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
            <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-slate-800 mb-1">Need to update organization settings?</p>
            <p className="text-sm text-slate-500">
              Contact your administrator to modify organization settings or change your role.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const MAIL_ALERT_CATEGORIES = [
  'PPE', 'FIRE', 'ZONE', 'VEHICLE', 'EQUIPMENT',
  'BEHAVIOR', 'HOUSEKEEPING', 'ENVIRONMENTAL', 'COMPLIANCE', 'PERMIT',
];
const MAIL_ALERT_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const MAIL_ALERT_ROLES = ['org_admin', 'site_manager', 'viewer'];
const COOLDOWN_PRESETS = [
  { label: 'Immediate', minutes: 0 },
  { label: '15 min', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: 'Daily', minutes: 1440 },
];

const severityPillConfig = {
  CRITICAL: { color: 'text-red-400', bg: 'bg-red-500/20' },
  HIGH: { color: 'text-orange-400', bg: 'bg-orange-500/20' },
  MEDIUM: { color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  LOW: { color: 'text-blue-400', bg: 'bg-blue-500/20' },
};

const ruleKey = (category, severity) => `${category}::${severity}`;

function MailAlertsSettings() {
  const [rules, setRules] = useState(null);
  const [usage, setUsage] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [openCategories, setOpenCategories] = useState(new Set());

  const toggleCategoryOpen = (category) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const [rulesRes, usageRes] = await Promise.all([
          notificationsAPI.getMailAlertRules(),
          notificationsAPI.getMailAlertUsage(),
        ]);
        setRules(rulesRes.data);
        setUsage(usageRes.data);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to load mail alert settings');
      }
    })();
  }, []);

  const updateLocalRule = (category, severity, patch) => {
    setRules(prev => prev.map(r => (
      r.category === category && r.severity === severity ? { ...r, ...patch, dirty: true } : r
    )));
  };

  const toggleRole = (category, severity, role) => {
    const rule = rules.find(r => r.category === category && r.severity === severity);
    const has = rule.roles.includes(role);
    updateLocalRule(category, severity, {
      roles: has ? rule.roles.filter(r => r !== role) : [...rule.roles, role],
    });
  };

  const saveRule = async (category, severity) => {
    const rule = rules.find(r => r.category === category && r.severity === severity);
    const key = ruleKey(category, severity);
    setSavingKey(key);
    try {
      const { data } = await notificationsAPI.updateMailAlertRule(category, severity, {
        roles: rule.roles,
        cooldownMinutes: rule.cooldownMinutes,
        isActive: rule.isActive,
      });
      setRules(prev => prev.map(r => (
        r.category === category && r.severity === severity ? { ...data, dirty: false } : r
      )));
      toast.success(`${category} / ${severity} mail alert rule saved`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save rule');
    } finally {
      setSavingKey(null);
    }
  };

  if (!rules) {
    return <div className="text-slate-500">Loading mail alert settings…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">Mail Alerts</h2>
        <p className="text-slate-500">
          Choose which severities email which roles, and how often — independently for each alert type.
        </p>
      </div>

      {usage && usage.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {usage.map(u => (
            <div key={u.siteId} className="glass rounded-xl p-3 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">{u.siteName}</span>
              {u.mailCapEnabled ? (
                <span className={clsx(
                  'text-xs font-mono px-2 py-1 rounded-lg',
                  u.percentage >= 100 ? 'bg-red-500/20 text-red-500' :
                    u.percentage >= 75 ? 'bg-amber-500/20 text-amber-600' :
                      'bg-slate-100 text-slate-500'
                )}>
                  {u.sentToday}/{u.cap} sent today
                </span>
              ) : (
                <span className="text-xs text-slate-400">No daily cap</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {MAIL_ALERT_CATEGORIES.map((category, index) => {
          const categoryRules = MAIL_ALERT_SEVERITIES
            .map(severity => rules.find(r => r.category === category && r.severity === severity))
            .filter(Boolean);
          if (categoryRules.length === 0) return null;

          const isOpen = openCategories.has(category);
          const activeCount = categoryRules.filter(r => r.isActive && r.roles.length > 0).length;

          return (
            <div
              key={category}
              className="glass rounded-xl overflow-hidden animate-fade-in"
              style={{ animationDelay: `${index * 0.03}s` }}
            >
              <button
                type="button"
                onClick={() => toggleCategoryOpen(category)}
                className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50/60 transition-colors"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-slate-800 capitalize">{category.toLowerCase()}</span>
                  <span className={clsx(
                    'text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0',
                    activeCount > 0 ? 'bg-[var(--theme-100)] text-[var(--theme-700)]' : 'bg-slate-100 text-slate-400'
                  )}>
                    {activeCount} of {categoryRules.length} active
                  </span>
                </span>
                <ChevronDownIcon className={clsx(
                  'w-5 h-5 text-slate-400 transition-transform flex-shrink-0',
                  isOpen && 'rotate-180'
                )} />
              </button>

              {isOpen && (
              <div className="space-y-3 px-4 pb-4">
                {categoryRules.map(rule => {
                  const config = severityPillConfig[rule.severity];
                  const key = ruleKey(rule.category, rule.severity);
                  return (
                    <div key={key} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between mb-3">
                        <span className={clsx(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold',
                          config.bg, config.color
                        )}>
                          {rule.severity}
                        </span>
                        <button
                          onClick={() => updateLocalRule(rule.category, rule.severity, { isActive: !rule.isActive })}
                          className={clsx(
                            'relative w-11 h-6 rounded-full transition-colors',
                            rule.isActive ? 'bg-[var(--theme-500)]' : 'bg-slate-200'
                          )}
                        >
                          <span className={clsx(
                            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                            rule.isActive && 'translate-x-5'
                          )} />
                        </button>
                      </div>

                      <div className={clsx('space-y-3', !rule.isActive && 'opacity-40 pointer-events-none')}>
                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Recipients</p>
                          <div className="flex flex-wrap gap-2">
                            {MAIL_ALERT_ROLES.map(role => {
                              const isEnabled = rule.roles.includes(role);
                              return (
                                <button
                                  key={role}
                                  onClick={() => toggleRole(rule.category, rule.severity, role)}
                                  className={clsx(
                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all',
                                    isEnabled
                                      ? 'bg-[var(--theme-100)] text-[var(--theme-700)] border border-[var(--theme-300)]'
                                      : 'bg-slate-50 text-slate-400 border border-slate-200 hover:border-slate-300'
                                  )}
                                >
                                  {isEnabled && <CheckIcon className="w-3.5 h-3.5" />}
                                  {role.replace(/_/g, ' ')}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-slate-500 mb-2">Cooldown between emails</p>
                          <div className="flex flex-wrap items-center gap-2">
                            {COOLDOWN_PRESETS.map(preset => (
                              <button
                                key={preset.minutes}
                                onClick={() => updateLocalRule(rule.category, rule.severity, { cooldownMinutes: preset.minutes })}
                                className={clsx(
                                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                                  rule.cooldownMinutes === preset.minutes
                                    ? 'bg-[var(--theme-100)] text-[var(--theme-700)] border border-[var(--theme-300)]'
                                    : 'bg-slate-50 text-slate-400 border border-slate-200 hover:border-slate-300'
                                )}
                              >
                                {preset.label}
                              </button>
                            ))}
                            <input
                              type="number"
                              min="0"
                              value={rule.cooldownMinutes}
                              onChange={(e) => updateLocalRule(rule.category, rule.severity, { cooldownMinutes: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                              className="input w-24 text-sm"
                            />
                            <span className="text-xs text-slate-400">minutes</span>
                          </div>
                        </div>
                      </div>

                      {rule.dirty && (
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={() => saveRule(rule.category, rule.severity)}
                            disabled={savingKey === key}
                            className="btn btn-primary btn-sm"
                          >
                            {savingKey === key ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppearanceSettings() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Appearance</h2>
        <p className="text-slate-500">Customize how KenSafety looks for you</p>
      </div>

      {/* Theme Selector */}
      <ThemeSelector />

      {/* Additional Appearance Options */}
      <div className="border-t border-slate-200 pt-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Display Options</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 glass rounded-xl cursor-pointer hover:bg-slate-50 transition-all">
            <div>
              <p className="font-medium text-slate-800">Compact Mode</p>
              <p className="text-sm text-slate-500">Reduce spacing for more content on screen</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
            />
          </label>
          <label className="flex items-center justify-between p-4 glass rounded-xl cursor-pointer hover:bg-slate-50 transition-all">
            <div>
              <p className="font-medium text-slate-800">Show Animations</p>
              <p className="text-sm text-slate-500">Enable smooth transitions and effects</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
            />
          </label>
          <label className="flex items-center justify-between p-4 glass rounded-xl cursor-pointer hover:bg-slate-50 transition-all">
            <div>
              <p className="font-medium text-slate-800">High Contrast Mode</p>
              <p className="text-sm text-slate-500">Increase contrast for better visibility</p>
            </div>
            <input
              type="checkbox"
              className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
            />
          </label>
        </div>
      </div>
    </div>
  );
}
